import { useState, useEffect } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { BarChart2, TrendingUp, Users, Eye, RefreshCw, Radar, CheckCircle2, XCircle, AlertTriangle, ExternalLink, RotateCw } from 'lucide-react';
import { TopNav } from '@/components/layout/TopNav';
import { SegmentedControl } from '@/components/ui/SegmentedControl';
import { Button } from '@/components/ui/Button';
import { analyticsApi, scrapeCreatorsApi, postsApi, type AnalyticsTopPost } from '@/lib/api';
import { useAppStore } from '@/stores/appStore';
import { useBackgroundRefresh } from '@/hooks/useBackgroundRefresh';
import { useWorkspaceRole, canEditWorkspace } from '@/hooks/useWorkspaceRole';
import { PLATFORMS } from '@/lib/platforms';
import { PlatformIcon } from '@/components/ui/PlatformIcon';
import { MonthlyReport } from '@/components/analytics/MonthlyReport';

const PERIOD_OPTIONS = [
  { label: '7 days', value: 7 },
  { label: '30 days', value: 30 },
  { label: '90 days', value: 90 },
];

export function Analytics() {
  const { getToken } = useAuth();
  const { currentWorkspace } = useAppStore();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [days, setDays] = useState(30);
  // ?tab=report deep-links straight to the Monthly Report (used by notifications).
  const [view, setView] = useState<'overview' | 'report'>(
    searchParams.get('tab') === 'report' ? 'report' : 'overview'
  );
  // Apply the deep link even when Analytics is already open, then drop the
  // param so following the same link again still switches the view.
  useEffect(() => {
    if (searchParams.get('tab') !== 'report') return;
    setView('report');
    const next = new URLSearchParams(searchParams);
    next.delete('tab');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  // Recycle a top performer: duplicate it and jump to Publishing where the
  // fresh copy is ready to re-schedule.
  const recyclePost = useMutation({
    mutationFn: async (postId: string) => {
      const token = await getToken();
      return postsApi.duplicate(token!, postId);
    },
    onSuccess: () => navigate('/publishing'),
  });

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['analytics-summary', currentWorkspace?.id, days],
    queryFn: async () => {
      const token = await getToken();
      return analyticsApi.summary(token!, currentWorkspace!.id, days);
    },
    enabled: !!currentWorkspace,
  });

  const { data: topPostsData } = useQuery({
    queryKey: ['analytics-top-posts', currentWorkspace?.id],
    queryFn: async () => {
      const token = await getToken();
      return analyticsApi.topPosts(token!, currentWorkspace!.id);
    },
    enabled: !!currentWorkspace,
  });

  const refresh = useBackgroundRefresh({
    trigger: async () => {
      const token = await getToken();
      return analyticsApi.refresh(token!, currentWorkspace!.id);
    },
    queryKeys: [
      ['analytics-summary', currentWorkspace?.id],
      ['analytics-top-posts', currentWorkspace?.id],
    ],
  });

  // Follower sync spends ScrapeCreators credits, so the API limits it to
  // owner/admin/editor; viewers don't get the button.
  const canSyncFollowers = canEditWorkspace(useWorkspaceRole());

  // Pull live follower counts from ScrapeCreators for supported social accounts.
  const syncFollowers = useMutation({
    mutationFn: async () => {
      const token = await getToken();
      return scrapeCreatorsApi.enrichAnalytics(token!, currentWorkspace!.id);
    },
    onSuccess: () => refetch(),
  });

  const summary = data;
  const topPosts = topPostsData?.posts ?? [];

  // Compute max daily count for chart scaling
  const dailyPosts = summary?.dailyPosts ?? [];
  const maxDaily = Math.max(...dailyPosts.map((d) => d.count), 1);

  // Platform bars max (scale by the busiest dimension so bars are meaningful
  // even for accounts that haven't published through the app)
  const platforms = summary?.platforms ?? [];
  const maxPlatformImpressions = Math.max(...platforms.map((p) => p.impressions), 1);
  const maxPlatformPublishes = Math.max(...platforms.map((p) => p.publishes), 1);

  // Platforms whose insights permission is missing (account synced but blocked).
  // Instagram is excluded: the app doesn't request instagram_manage_insights,
  // so reconnecting can't help there.
  const insightsBlocked = platforms
    .filter((p) => p.status === 'insights_unavailable' && p.platform !== 'instagram')
    .map((p) => p.platform);

  if (!currentWorkspace) {
    return (
      <div className="flex-1 overflow-auto flex flex-col">
        <TopNav title="Analytics" />
        <div className="flex items-center justify-center h-[calc(100vh-3.5rem)]">
          <p className="text-sm text-muted-foreground">Select a workspace to view analytics</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-auto flex flex-col">
      <TopNav title="Analytics" />

      <div className="p-6 space-y-6 mx-auto w-full max-w-[1400px]">
        {/* Overview / Monthly Report switch */}
        <SegmentedControl
          aria-label="Analytics view"
          options={[
            { label: 'Overview', value: 'overview' as const },
            { label: 'Monthly Report', value: 'report' as const },
          ]}
          value={view}
          onChange={setView}
        />

        {view === 'report' ? (
          <MonthlyReport />
        ) : (
          <>
        {/* Header controls */}
        <div className="flex items-center justify-between">
          <SegmentedControl
            aria-label="Time period"
            options={PERIOD_OPTIONS}
            value={days}
            onChange={setDays}
          />
          <div className="flex items-center gap-2">
            {canSyncFollowers && (
              <Button
                variant="secondary"
                onClick={() => syncFollowers.mutate()}
                loading={syncFollowers.isPending}
                title="Fetch live follower counts via ScrapeCreators"
              >
                {!syncFollowers.isPending && <Radar className="h-3.5 w-3.5" />}
                Sync followers
              </Button>
            )}
            <Button
              variant="secondary"
              onClick={() => refresh.start()}
              loading={refresh.isRefreshing}
              title="Pull live impressions, engagement and followers from each platform"
            >
              {!refresh.isRefreshing && <RefreshCw className="h-3.5 w-3.5" />}
              {refresh.isRefreshing ? 'Refreshing…' : 'Refresh'}
            </Button>
          </div>
        </div>

        {/* Sync / status feedback */}
        {summary?.lastSyncedAt && (
          <p className="text-xs text-muted-foreground -mt-2">
            Last synced {new Date(summary.lastSyncedAt).toLocaleString()} · {summary.accountsSynced} account
            {summary.accountsSynced !== 1 ? 's' : ''}
          </p>
        )}
        {syncFollowers.data && (
          <p className="text-xs text-muted-foreground -mt-2">
            Synced {syncFollowers.data.enriched} of {syncFollowers.data.total} account
            {syncFollowers.data.total !== 1 ? 's' : ''}
            {syncFollowers.data.skipped > 0 && ` · ${syncFollowers.data.skipped} unsupported`}
            {syncFollowers.data.errors.length > 0 && (
              <span className="text-destructive"> · {syncFollowers.data.errors.length} failed</span>
            )}
          </p>
        )}
        {syncFollowers.isError && (
          <p className="text-xs text-destructive -mt-2">{syncFollowers.error?.message || 'Failed to sync followers.'}</p>
        )}

        {/* Missing-insights-permission hint */}
        {insightsBlocked.length > 0 && (
          <div className="flex items-start gap-2 rounded-lg ring-1 ring-warning/30 bg-warning/10 px-3 py-2.5 text-xs text-foreground -mt-2">
            <AlertTriangle className="h-4 w-4 text-warning shrink-0 mt-0.5" />
            <span>
              Impressions &amp; engagement need the insights permission on{' '}
              <span className="font-medium">
                {insightsBlocked.map((p) => PLATFORMS[p as keyof typeof PLATFORMS]?.label ?? p).join(', ')}
              </span>
              . Reconnect{' '}
              {insightsBlocked.length > 1 ? 'these accounts' : 'this account'} in Settings → Social Accounts to enable them.
            </span>
          </div>
        )}

        {isLoading ? (
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-32 bg-muted rounded-lg animate-pulse" />
            ))}
          </div>
        ) : (
          <>
            {/* Stats grid */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard
                label="Posts Published"
                value={summary?.posts.published ?? 0}
                icon={BarChart2}
                color="text-info"
                bg="bg-info/10"
              />
              <StatCard
                label="Impressions"
                value={formatNumber(summary?.reach.impressions ?? 0)}
                icon={Eye}
                color="text-accent"
                bg="bg-accent/10"
              />
              <StatCard
                label="Engagements"
                value={formatNumber(summary?.reach.engagements ?? 0)}
                icon={TrendingUp}
                color="text-success"
                bg="bg-success/10"
              />
              <StatCard
                label="Followers"
                value={formatNumber(summary?.reach.followers ?? 0)}
                icon={Users}
                color="text-warning"
                bg="bg-warning/10"
              />
            </div>

            {/* Publish success/fail */}
            {(summary?.posts.publishTotal ?? 0) > 0 && (
              <div className="bg-card border border-border rounded-lg p-4 flex items-center gap-6">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-success" />
                  <span className="text-sm text-foreground font-medium">
                    {summary?.posts.publishSuccess ?? 0}
                  </span>
                  <span className="text-xs text-muted-foreground">successful publishes</span>
                </div>
                <div className="flex items-center gap-2">
                  <XCircle className="h-4 w-4 text-destructive" />
                  <span className="text-sm text-foreground font-medium">
                    {summary?.posts.publishFailed ?? 0}
                  </span>
                  <span className="text-xs text-muted-foreground">failed</span>
                </div>
                <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                  <div
                    className="h-full bg-success rounded-full"
                    style={{
                      width: `${((summary?.posts.publishSuccess ?? 0) / (summary?.posts.publishTotal ?? 1)) * 100}%`,
                    }}
                  />
                </div>
              </div>
            )}

            {/* Daily posts chart */}
            <div className="bg-card border border-border rounded-lg p-6">
              <h3 className="text-sm font-semibold text-foreground mb-4">Posts Over Time</h3>
              {dailyPosts.length === 0 ? (
                <div className="flex items-center justify-center h-32 text-muted-foreground">
                  <p className="text-sm">No published posts in this period</p>
                </div>
              ) : (
                <div className="flex items-end gap-1 h-32">
                  {dailyPosts.map((day) => (
                    <div
                      key={day.date}
                      className="flex-1 flex flex-col items-center gap-1 group"
                      title={`${day.date}: ${day.count} post${day.count !== 1 ? 's' : ''}`}
                    >
                      <div className="w-full flex items-end justify-center" style={{ height: '100px' }}>
                        <div
                          className="w-full bg-accent/60 group-hover:bg-accent rounded-t transition-colors"
                          style={{ height: `${(day.count / maxDaily) * 100}%`, minHeight: '2px' }}
                        />
                      </div>
                      {dailyPosts.length <= 14 && (
                        <span className="text-[9px] text-muted-foreground rotate-45 origin-left whitespace-nowrap">
                          {day.date.slice(5)}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Platform breakdown */}
            <div className="bg-card border border-border rounded-lg p-6">
              <h3 className="text-sm font-semibold text-foreground mb-4">Platform Performance</h3>
              {platforms.length === 0 ? (
                <div className="flex items-center justify-center h-24 text-muted-foreground">
                  <p className="text-sm">No platform data yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {platforms.map((p) => {
                    const platform = PLATFORMS[p.platform as keyof typeof PLATFORMS];
                    const successRate = p.publishes > 0 ? (p.success / p.publishes) * 100 : 0;
                    // Scale the bar by reach (impressions) when we have it, otherwise
                    // fall back to publish volume so the bar still reads.
                    const barPct =
                      p.impressions > 0
                        ? (p.impressions / maxPlatformImpressions) * 100
                        : (p.publishes / maxPlatformPublishes) * 100;
                    return (
                      <div key={p.platform} className="space-y-1.5">
                        <div className="flex items-center justify-between text-xs">
                          <span className="flex items-center gap-1.5 text-foreground font-medium">
                            <PlatformIcon platform={p.platform} variant="glyph" size="xs" />
                            {platform?.label ?? p.platform}
                          </span>
                          <span className="text-muted-foreground">
                            {p.publishes > 0 ? `${p.success}/${p.publishes} published` : `${formatNumber(p.followers)} followers`}
                          </span>
                        </div>
                        <div className="h-2 bg-muted rounded-full overflow-hidden">
                          <div
                            className="h-full rounded-full transition-all"
                            style={{
                              width: `${Math.max(barPct, 2)}%`,
                              backgroundColor: platform?.color ?? 'hsl(var(--accent))',
                            }}
                          />
                        </div>
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-0.5 text-[10px] text-muted-foreground">
                          {p.publishes > 0 && <span>{successRate.toFixed(0)}% success rate</span>}
                          <span><span className="text-foreground font-medium">{formatNumber(p.impressions)}</span> impressions</span>
                          <span><span className="text-foreground font-medium">{formatNumber(p.engagements)}</span> engagements</span>
                          <span><span className="text-foreground font-medium">{formatNumber(p.followers)}</span> followers</span>
                          {p.status === 'insights_unavailable' && (
                            p.platform === 'instagram' ? (
                              <span className="text-muted-foreground">reach &amp; impressions not yet available for Instagram</span>
                            ) : (
                              <span className="text-warning">insights unavailable — reconnect</span>
                            )
                          )}
                          {p.status === 'unauthorized' && (
                            <span className="text-warning">sync failing — reconnect this account</span>
                          )}
                          {p.status === 'rate_limited' && (
                            <span className="text-warning">platform rate limit hit — figures may be out of date</span>
                          )}
                          {p.status === 'error' && (
                            <span className="text-warning">latest sync failed — figures may be out of date</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Top posts */}
            <div className="bg-card border border-border rounded-lg p-6">
              <h3 className="text-sm font-semibold text-foreground mb-4">Top Performing Posts</h3>
              {topPosts.length === 0 ? (
                <div className="flex items-center justify-center h-24 text-muted-foreground">
                  <p className="text-sm">No published posts yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {topPosts.map((post: AnalyticsTopPost, i) => {
                    const postPlatforms: string[] = JSON.parse(post.platforms || '[]');
                    return (
                      <div
                        key={post.id}
                        className="flex items-start gap-3 p-3 bg-muted/40 ring-1 ring-border/50 rounded-lg"
                      >
                        <span className="text-sm font-bold text-muted-foreground w-6 shrink-0">
                          #{i + 1}
                        </span>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-foreground line-clamp-2">
                            {post.content || <span className="italic text-muted-foreground">(no caption)</span>}
                          </p>
                          <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                            {post.published_at && (
                              <span className="text-[10px] text-muted-foreground">
                                {new Date(post.published_at).toLocaleDateString()}
                              </span>
                            )}
                            {postPlatforms.slice(0, 3).map((pl) => (
                              <span
                                key={pl}
                                className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-tertiary text-muted-foreground"
                              >
                                <PlatformIcon platform={pl} variant="glyph" size="xs" />
                                {PLATFORMS[pl as keyof typeof PLATFORMS]?.label ?? pl}
                              </span>
                            ))}
                            {post.source === 'organic' && (
                              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-accent/10 text-accent">
                                organic
                              </span>
                            )}
                            {post.permalink && (
                              <a
                                href={post.permalink}
                                target="_blank"
                                rel="noreferrer"
                                className="text-[10px] text-accent hover:underline inline-flex items-center gap-0.5"
                              >
                                view <ExternalLink className="h-2.5 w-2.5" />
                              </a>
                            )}
                            {post.source === 'app' && (
                              <button
                                onClick={() => recyclePost.mutate(post.id)}
                                disabled={recyclePost.isPending}
                                className="text-[10px] text-accent hover:underline inline-flex items-center gap-0.5 disabled:opacity-50"
                                title="Duplicate this winner and open it to re-schedule"
                              >
                                <RotateCw className="h-2.5 w-2.5" /> recycle
                              </button>
                            )}
                          </div>
                        </div>
                        <div className="text-right shrink-0">
                          {post.engagements > 0 || post.impressions > 0 ? (
                            <>
                              <p className="text-xs font-semibold text-success">
                                {formatNumber(post.engagements)}
                              </p>
                              <p className="text-[10px] text-muted-foreground">
                                engagements{post.impressions > 0 ? ` · ${formatNumber(post.impressions)} impr` : ''}
                              </p>
                            </>
                          ) : (
                            <>
                              <p className="text-xs font-semibold text-success">
                                {post.success_count}/{post.platform_count}
                              </p>
                              <p className="text-[10px] text-muted-foreground">platforms</p>
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </>
        )}
          </>
        )}
      </div>
    </div>
  );
}

function StatCard({
  label,
  value,
  icon: Icon,
  color,
  bg,
}: {
  label: string;
  value: string | number;
  icon: React.ElementType;
  color: string;
  bg: string;
}) {
  return (
    <div className="bg-card border border-border rounded-lg p-4">
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs text-muted-foreground">{label}</span>
        <div className={`h-7 w-7 rounded-md ${bg} flex items-center justify-center`}>
          <Icon className={`h-4 w-4 ${color}`} />
        </div>
      </div>
      <p className="text-2xl font-bold text-foreground">{value}</p>
    </div>
  );
}

function formatNumber(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}
